import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, X, Expand } from "lucide-react";
import { SubNav } from "@/components/case-study/CaseChrome";
import Footer from "@/components/Footer";

const SEV = {
  critical: { label: "Critical", color: "#B91C1C" },
  high: { label: "High", color: "#EA580C" },
  medium: { label: "Medium", color: "#CA8A04" },
  low: { label: "Low", color: "#4C1D95" },
};
const EFFORT_SCORE = { S: 22, M: 52, L: 82 };
const IMPACT_SCORE = { critical: 86, high: 64, medium: 40, low: 22 };

const competitors = ["iHomeFinder", "AgentFire", "BoldTrail", "Luxury Presence"];
const funnels = ["Signup", "Plan Selection", "Compare Plans", "Take a Tour", "Direct Order"];

const IDX = "/case-studies/idx";
const teardown = [
  { src: `${IDX}/teardown-idx.webp`, label: "IDX Broker", note: "The audited product — signup reads like an order form, pricing is a dense grid, and the hero has low-contrast overlay text." },
  { src: `${IDX}/teardown-boldtrail.webp`, label: "BoldTrail", note: "Staged signup, a transparent stepped checkout, and a strong, modern visual system." },
  { src: `${IDX}/teardown-agentfire.webp`, label: "AgentFire", note: "Booking-first funnel, modular feature layout, and embedded demos shown in context." },
  { src: `${IDX}/teardown-luxurypresence.webp`, label: "Luxury Presence", note: "A minimal, high-trust form and polished layout — though no public pricing or self-serve tour." },
];
const goodExamples = [
  { src: `${IDX}/ex-boldtrail-pricing.webp`, competitor: "BoldTrail", pattern: "Scannable plan comparison",
    why: "Badged, icon-led tiers let a buyer compare features at a glance instead of reading a wall of text.",
    apply: "Rebuild IDX's plan grid as three icon-led cards with a highlighted 'most popular' tier." },
  { src: `${IDX}/ex-agentfire-pricing.webp`, competitor: "AgentFire", pattern: "Stepped, progressive pricing",
    why: "Design first, then plan — one decision per step keeps cognitive load low and momentum high.",
    apply: "Split IDX's order flow into staged steps with a running summary of what's selected so far." },
  { src: `${IDX}/ex-boldtrail-modal.webp`, competitor: "BoldTrail", pattern: "Staged lead capture",
    why: "A short, contextual capture step replaces one long order screen and cuts early drop-off.",
    apply: "Lead with a quick email + name capture, then move detailed setup to after the first commitment." },
  { src: `${IDX}/ex-luxurypresence-hero.webp`, competitor: "Luxury Presence", pattern: "High-contrast hero",
    why: "A bold headline on a clean area with real whitespace makes the value obvious in seconds.",
    apply: "Drop the stock-photo text overlay; use a bold headline block beside a single product visual." },
];

const findings = [
  { id: "F01", funnel: "Signup", severity: "critical", effort: "M", desc: "Signup is one long form instead of staged steps.",
    observation: "New users hit a single, lengthy form demanding extensive information before any value is shown.",
    userImpact: "Perceived complexity and the upfront data ask make users abandon before they understand the product.",
    businessImpact: "High signup drop-off caps every downstream conversion.",
    recommendation: "Split signup into a lead-capture stage (email, name, 'Get started') and a later setup stage after the initial commitment.",
    bench: "AgentFire and BoldTrail stage their signup." },
  { id: "F02", funnel: "Direct Order", severity: "critical", effort: "M", desc: "Order flow has no progress, back navigation, or live price.",
    observation: "Users can't see how many steps remain or how the total changes as they add options.",
    userImpact: "Uncertainty about cost and stage creates anxiety and cart abandonment.",
    businessImpact: "Opaque checkout directly loses completed orders.",
    recommendation: "Add a progress bar, back navigation, and a live price summary that updates as add-ons change.",
    bench: "BoldTrail's checkout is transparent and stepped." },
  { id: "F03", funnel: "Compare Plans", severity: "critical", effort: "M", desc: "Plan comparison is a dense long-text grid.",
    observation: "Features are compared as walls of text with no icons or scannable hierarchy.",
    userImpact: "Users can't compare plans at a glance, causing decision fatigue and low confidence.",
    businessImpact: "Hard comparison pushes users to a suboptimal plan or none at all.",
    recommendation: "Replace the text grid with icon-based feature rows, clear headings, and one column per plan.",
    bench: "iHomeFinder and Luxury Presence compare visually." },
  { id: "F04", funnel: "Plan Selection", severity: "high", effort: "M", desc: "Plans lack visual distinction and hierarchy.",
    observation: "Tiers render without cards, badges, or grouping to separate them.",
    userImpact: "Users can't quickly identify the right plan and stall on the decision.",
    businessImpact: "Weak plan clarity lowers selection and upgrade rates.",
    recommendation: "Use a card layout per plan with distinct tiers, badges, and grouped, icon-led feature lists.",
    bench: "Competitors use badged pricing cards." },
  { id: "F05", funnel: "Take a Tour", severity: "high", effort: "M", desc: "The product tour is passive and generic.",
    observation: "The tour lacks narration and role-specific paths.",
    userImpact: "Users don't grasp the platform's value, so adoption stays low.",
    businessImpact: "A skippable tour wastes the best moment to sell capability.",
    recommendation: "Add a narrated walkthrough and user-specific interactive paths (solo agent, team lead).",
    bench: "BoldTrail and Luxury Presence offer interactive demos." },
  { id: "F06", funnel: "Plan Selection", severity: "high", effort: "S", desc: "Add-ons and upgrades aren't clearly delineated.",
    observation: "Add-ons lack labels, badges, and pricing clarity.",
    userImpact: "Users miss valuable add-ons or get confused about cost and purpose.",
    businessImpact: "Unclear upgrades leave revenue on the table.",
    recommendation: "Give add-ons a dedicated, clearly-labelled section with badges ('Popular') and tooltips.",
    bench: "Competitors badge and explain add-ons inline." },
  { id: "F07", funnel: "Signup", severity: "high", effort: "S", desc: "Hero overlays text on images, hurting readability.",
    observation: "The hero places copy over background imagery, reducing legibility and accessibility.",
    userImpact: "The core value message is missed or misread.",
    businessImpact: "A weak first impression drags top-of-funnel conversion.",
    recommendation: "Move to bold headings on a clean area or side-by-side layout with more whitespace.",
    bench: "Luxury Presence and AgentFire use clean hero layouts." },
  { id: "F08", funnel: "General", severity: "high", effort: "S", desc: "Low-contrast blue text on white fails readability.",
    observation: "Body text uses low-contrast blue on white backgrounds.",
    userImpact: "Users with low vision or poor lighting struggle to read content.",
    businessImpact: "Accessibility gaps risk compliance and erode trust.",
    recommendation: "Raise contrast to meet WCAG (≥4.5:1 for body text).",
    bench: "Baseline accessibility all competitors meet." },
  { id: "F09", funnel: "General", severity: "medium", effort: "M", desc: "Inconsistent visual hierarchy across pages.",
    observation: "Headings, weights, and dividers are applied inconsistently.",
    userImpact: "Users can't tell what matters and navigate inefficiently.",
    businessImpact: "Slower comprehension reduces engagement site-wide.",
    recommendation: "Standardise headings, font weights, and section dividers into a clear type scale.",
    bench: "Competitors keep a consistent hierarchy." },
  { id: "F10", funnel: "Take a Tour", severity: "medium", effort: "M", desc: "Tour doesn't show user-specific filters or dynamic content.",
    observation: "The tour is one generic path for everyone.",
    userImpact: "Users don't see how the platform fits their role, lowering relevance.",
    businessImpact: "Generic tours convert worse than tailored ones.",
    recommendation: "Add short animated previews showing user-specific filters and settings in action.",
    bench: "Competitors tailor the demo by user type." },
  { id: "F11", funnel: "General", severity: "medium", effort: "L", desc: "UI feels dated and cluttered.",
    observation: "Elements lack whitespace, depth, and modern iconography.",
    userImpact: "The platform reads as less professional than rivals.",
    businessImpact: "Dated UI weakens brand perception and trust at decision time.",
    recommendation: "Introduce whitespace, soft shadows, and clean icons as a reusable component pattern.",
    bench: "Competitors present a modern, uncluttered UI." },
  { id: "F12", funnel: "Plan Selection", severity: "medium", effort: "S", desc: "Benefits are in a carousel users overlook.",
    observation: "Key benefits sit inside a rotating carousel.",
    userImpact: "Carousels are skipped, so the value proposition is missed.",
    businessImpact: "Buried benefits weaken the case to buy.",
    recommendation: "Replace the carousel with a static, scannable benefits grid with icons.",
    bench: "Competitors use static benefit grids." },
];

function SevChip({ severity }) {
  const s = SEV[severity];
  return <span className="inline-flex items-center label-mono text-white px-2 py-0.5" style={{ background: s.color }}>{s.label}</span>;
}
function EffortChip({ effort }) {
  return <span className="inline-flex items-center label-mono border border-[#0A0A0A] px-2 py-0.5">{effort} effort</span>;
}

function Matrix() {
  const [active, setActive] = useState(null);
  return (
    <div className="relative w-full max-w-[560px] mx-auto lg:mx-0 aspect-square border border-[#0A0A0A] bg-white">
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#0A0A0A]/20" />
      <div className="absolute top-1/2 left-0 right-0 h-px bg-[#0A0A0A]/20" />
      <span className="absolute top-2 left-2 label-mono text-[#4C1D95]">Quick wins</span>
      <span className="absolute top-2 right-2 label-mono text-[#595959]">Major projects</span>
      <span className="absolute bottom-2 left-2 label-mono text-[#595959]">Fill-ins</span>
      <span className="absolute bottom-2 right-2 label-mono text-[#595959]">Reconsider</span>
      <span className="absolute bottom-[-22px] left-1/2 -translate-x-1/2 label-mono text-[#8A8A8A]">Effort →</span>
      {findings.map((f) => (
        <button key={f.id} type="button"
          onMouseEnter={() => setActive(f.id)} onMouseLeave={() => setActive((a) => (a === f.id ? null : a))}
          onFocus={() => setActive(f.id)} onClick={() => setActive(f.id)}
          className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white cursor-pointer transition-transform hover:scale-125 focus:scale-125 outline-none"
          style={{ left: `${EFFORT_SCORE[f.effort]}%`, top: `${100 - IMPACT_SCORE[f.severity]}%`, background: SEV[f.severity].color }}
          data-testid={`matrix-dot-${f.id}`} aria-label={`${f.id}: ${f.desc}`}>
          {active === f.id && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-5 whitespace-nowrap bg-[#0A0A0A] text-white label-mono px-2 py-1 z-10 pointer-events-none max-w-[240px] truncate">{f.id} · {f.desc}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function Gallery({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {items.map((it, i) => (
          <figure key={it.src} className="border border-[#0A0A0A] bg-white flex flex-col">
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="group relative block overflow-hidden border-b border-[#0A0A0A] bg-[#F5F3FF]"
              data-testid={`idx-gallery-item-${i}`}
              aria-label={`Enlarge ${it.label}`}
            >
              <img src={it.src} alt={it.label} loading="lazy" className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]" />
              <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 label-mono bg-[#0A0A0A] text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand size={12} strokeWidth={1.5} /> Expand
              </span>
            </button>
            <figcaption className="p-5">
              <p className="font-display text-xl tracking-tighter mb-1.5">{it.label}</p>
              <p className="text-sm leading-relaxed text-[#52525B]">{it.note}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      {open !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0A0A]/90 flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpen(null)}
          data-testid="idx-lightbox"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="absolute top-5 right-5 text-white border border-white/40 p-2 hover:bg-white hover:text-[#0A0A0A] transition-colors"
            aria-label="Close"
            data-testid="idx-lightbox-close"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
          <figure className="max-w-[1100px] w-full" onClick={(e) => e.stopPropagation()}>
            <img src={items[open].src} alt={items[open].label} className="w-full h-auto max-h-[78vh] object-contain bg-white border border-white/20" />
            <figcaption className="text-center mt-4">
              <span className="label-mono text-[#DDD6FE]">{items[open].label}</span>
              <span className="text-sm text-white/70"> — {items[open].note}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}

function GoodExamples({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {items.map((it, i) => (
          <figure key={it.src} className="border border-[#0A0A0A] bg-white flex flex-col">
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="group relative block w-full aspect-[16/10] overflow-hidden border-b border-[#0A0A0A] bg-[#F5F3FF]"
              data-testid={`idx-good-item-${i}`}
              aria-label={`Enlarge ${it.competitor} — ${it.pattern}`}
            >
              <img
                src={it.src}
                alt={`${it.competitor} — ${it.pattern}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 label-mono bg-[#0A0A0A] text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand size={12} strokeWidth={1.5} /> Expand
              </span>
            </button>
            <figcaption className="p-6 flex flex-col flex-1">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="label-mono text-white bg-[#4C1D95] px-2 py-0.5">{it.competitor}</span>
                <span className="label-mono text-[#595959]">{it.pattern}</span>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-[#52525B] mb-5">{it.why}</p>
              <div className="mt-auto border-l-2 border-[#4C1D95] pl-3">
                <p className="label-mono text-[#0A0A0A] mb-1">Apply to IDX Broker</p>
                <p className="text-sm leading-relaxed text-[#0A0A0A]">{it.apply}</p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
      {open !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0A0A]/90 flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpen(null)}
          data-testid="idx-good-lightbox"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="absolute top-5 right-5 text-white border border-white/40 p-2 hover:bg-white hover:text-[#0A0A0A] transition-colors"
            aria-label="Close"
            data-testid="idx-good-lightbox-close"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
          <figure className="max-w-[1100px] w-full" onClick={(e) => e.stopPropagation()}>
            <img src={items[open].src} alt={`${items[open].competitor} — ${items[open].pattern}`} className="w-full h-auto max-h-[78vh] object-contain bg-white border border-white/20" />
            <figcaption className="text-center mt-4">
              <span className="label-mono text-[#DDD6FE]">{items[open].competitor} — {items[open].pattern}</span>
              <span className="text-sm text-white/70"> — {items[open].why}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}


export default function IdxBrokerAudit() {
  const critical = findings.filter((f) => f.severity === "critical").length;
  const high = findings.filter((f) => f.severity === "high").length;
  const medium = findings.filter((f) => f.severity === "medium").length;

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]" data-testid="case-study-page">
      <SubNav />
      <main>
        {/* Hero */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
              <p className="label-mono text-[#595959]">/ 05 — Case study</p>
              <p className="label-mono text-[#595959]">IDX Broker</p>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter">IDX Broker — Competitive UX Audit</h1>
            <p className="font-display text-2xl md:text-3xl tracking-tighter text-[#4C1D95] mt-4 max-w-3xl">Benchmarked against four competitors across five conversion funnels.</p>
            <p className="max-w-3xl text-lg md:text-xl leading-relaxed mt-8 text-[#0A0A0A]">
              This UX benchmark evaluates IDX Broker against iHomeFinder, AgentFire, BoldTrail and Luxury Presence across the Signup, Plan Selection, Compare Plans, Take a Tour and Direct Order funnels. The objective: uncover usability gaps, surface missed conversion opportunities, and give a prioritised set of fixes to raise IDX Broker&apos;s competitive positioning.
            </p>
          </div>
        </section>

        {/* 1 — Summary bar */}
        <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white" data-testid="audit-summary">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-12 grid grid-cols-2 md:grid-cols-5 gap-y-8">
            {[
              { n: "4", l: "Competitors benchmarked" },
              { n: "5", l: "Funnels analysed" },
              { n: "12", l: "Findings logged" },
              { n: `${critical} / ${high} / ${medium}`, l: "Critical / High / Medium" },
              { n: "Heuristic", l: "Comparative method" },
            ].map((s) => (
              <div key={s.l} className="px-2">
                <div className="font-display text-3xl md:text-4xl tracking-tighter">{s.n}</div>
                <div className="label-mono text-[#DDD6FE] mt-2 max-w-[18ch]">{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* 2 — Scope & method */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 01 — Scope &amp; method</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tighter mb-5">What was benchmarked</h2>
                <p className="label-mono text-[#595959] mb-2">Funnels</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {funnels.map((t) => <span key={t} className="label-mono border border-[#0A0A0A] px-2 py-1">{t}</span>)}
                </div>
                <p className="label-mono text-[#595959] mb-2">Competitors</p>
                <div className="flex flex-wrap gap-2">
                  {competitors.map((t) => <span key={t} className="label-mono border border-[#0A0A0A] px-2 py-1">{t}</span>)}
                </div>
              </div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tighter mb-5">How it was reviewed</h2>
                <p className="text-base md:text-lg leading-relaxed text-[#3F3F46] mb-6">Comparative heuristic evaluation — walking each funnel on IDX Broker and every competitor, logging where IDX falls short. Each finding is rated on a defined severity scale:</p>
                <ul className="space-y-2.5 mb-6">
                  {[["critical", "Blocks or loses the conversion"], ["high", "Causes drop-off"], ["medium", "Adds friction"], ["low", "Polish"]].map(([k, d]) => (
                    <li key={k} className="flex items-center gap-3"><SevChip severity={k} /><span className="text-base text-[#3F3F46]">{d}</span></li>
                  ))}
                </ul>
                <p className="text-sm leading-relaxed text-[#595959] border-l-2 border-[#4C1D95] pl-4">Not in scope: no moderated user testing and no analytics access — findings are comparative expert evaluation, not behavioural proof.</p>
              </div>
            </div>
            {/* Product under review */}
            <div className="mt-16 md:mt-20 pt-12 border-t border-[#0A0A0A]/15">
              <p className="label-mono text-[#595959] mb-6">The product under review</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                <figure className="md:col-span-2 border border-[#0A0A0A] bg-white">
                  <img src={`${IDX}/idx-homepage.webp`} alt="IDX Broker current homepage" loading="lazy" className="w-full h-auto block border-b border-[#0A0A0A]" />
                  <figcaption className="p-4 text-sm leading-relaxed text-[#52525B]">IDX Broker&apos;s current homepage — a stock-photo hero with low-contrast overlay text and no clear signup path.</figcaption>
                </figure>
                <figure className="border border-[#0A0A0A] bg-white">
                  <img src={`${IDX}/idx-pricing.webp`} alt="IDX Broker plans and pricing" loading="lazy" className="w-full h-auto block border-b border-[#0A0A0A]" />
                  <figcaption className="p-4 text-sm leading-relaxed text-[#52525B]">Plans render as a dense, text-heavy grid that&apos;s hard to compare at a glance.</figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        {/* 3 — Findings index */}
        <section className="border-b border-[#0A0A0A] bg-[#F5F3FF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 02 — Findings index</p>
            <table className="hidden md:table w-full border-t border-l border-[#0A0A0A] text-left">
              <thead><tr className="label-mono text-[#595959]">{["ID", "Funnel", "Finding", "Severity", "Effort"].map((h) => <th key={h} className="border-b border-r border-[#0A0A0A] p-3 bg-white">{h}</th>)}</tr></thead>
              <tbody>
                {findings.map((f) => (
                  <tr key={f.id} className="align-top hover:bg-white transition-colors">
                    <td className="border-b border-r border-[#0A0A0A] p-3"><a href={`#${f.id}`} className="font-display text-lg text-[#4C1D95] hover:underline">{f.id}</a></td>
                    <td className="border-b border-r border-[#0A0A0A] p-3 text-sm">{f.funnel}</td>
                    <td className="border-b border-r border-[#0A0A0A] p-3 text-sm max-w-[460px]">{f.desc}</td>
                    <td className="border-b border-r border-[#0A0A0A] p-3"><SevChip severity={f.severity} /></td>
                    <td className="border-b border-r border-[#0A0A0A] p-3"><EffortChip effort={f.effort} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="md:hidden space-y-3">
              {findings.map((f) => (
                <a key={f.id} href={`#${f.id}`} className="block border border-[#0A0A0A] bg-white p-4">
                  <div className="flex items-center justify-between mb-2"><span className="font-display text-xl text-[#4C1D95]">{f.id}</span><div className="flex gap-2"><SevChip severity={f.severity} /><EffortChip effort={f.effort} /></div></div>
                  <p className="text-sm mb-1">{f.desc}</p><p className="label-mono text-[#595959]">{f.funnel}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 4 — Finding detail blocks */}
        <section className="border-b border-[#0A0A0A]" data-testid="finding-details">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-16 md:pt-24"><p className="label-mono text-[#4C1D95]">/ 03 — Findings in detail</p></div>
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {findings.map((f) => (
              <div key={f.id} id={f.id} className="scroll-mt-20 border border-[#0A0A0A] bg-white p-6 md:p-8 flex flex-col">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="font-display text-3xl tracking-tighter text-[#4C1D95]">{f.id}</span>
                  <SevChip severity={f.severity} /><EffortChip effort={f.effort} />
                  <span className="label-mono text-[#595959]">{f.funnel}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl tracking-tighter mb-5">{f.desc}</h3>
                <div className="space-y-4">
                  <div><p className="label-mono text-[#0A0A0A] mb-1">Observation</p><p className="text-sm md:text-base leading-relaxed text-[#52525B]">{f.observation}</p></div>
                  <div><p className="label-mono text-[#0A0A0A] mb-1">Why it matters</p>
                    <p className="text-sm md:text-base leading-relaxed text-[#52525B]"><span className="text-[#0A0A0A] font-semibold">User: </span>{f.userImpact}</p>
                    <p className="text-sm md:text-base leading-relaxed text-[#52525B] mt-1"><span className="text-[#0A0A0A] font-semibold">Business: </span>{f.businessImpact}</p>
                  </div>
                  <div><p className="label-mono text-[#0A0A0A] mb-1">Recommendation</p><p className="text-sm md:text-base leading-relaxed text-[#0A0A0A]">{f.recommendation}</p></div>
                  <div className="border-l-2 border-[#4C1D95] pl-3"><p className="label-mono text-[#595959]">Benchmark</p><p className="text-sm leading-relaxed text-[#52525B]">{f.bench}</p></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 4 — Funnel teardown */}
        <section className="border-b border-[#0A0A0A]" data-testid="idx-teardown">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-4">/ 04 — Funnel teardown</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter mb-6 max-w-3xl">IDX Broker vs. the field<span className="text-[#4C1D95]">.</span></h2>
            <p className="text-lg leading-relaxed text-[#3F3F46] mb-10 max-w-2xl">Each product was walked through Sign-up, Tour, Order / Pricing and Visual &amp; Layout — green marks what works, red marks where it loses users. Click any board to enlarge.</p>
            <Gallery items={teardown} />
            <div className="mt-16 md:mt-20 pt-12 border-t border-[#0A0A0A]/15">
              <p className="label-mono text-[#4C1D95] mb-4">What good looks like</p>
              <h3 className="font-display text-2xl md:text-3xl tracking-tighter mb-4 max-w-2xl">The patterns IDX Broker should borrow.</h3>
              <p className="text-base md:text-lg leading-relaxed text-[#3F3F46] mb-10 max-w-2xl">Four moves competitors already get right — and exactly how each one translates into a concrete fix for IDX Broker.</p>
              <GoodExamples items={goodExamples} />
            </div>
          </div>
        </section>

        {/* 5 — Matrix */}
        <section className="border-b border-[#0A0A0A] bg-[#F5F3FF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 05 — Impact vs. effort</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-display text-3xl md:text-5xl tracking-tighter mb-6">Where to start<span className="text-[#4C1D95]">.</span></h2>
                <p className="text-lg leading-relaxed text-[#3F3F46] mb-6 max-w-md">Every finding plotted by expected impact against effort to fix. Hover or tap a dot. Top-left — high impact, low effort — is where IDX Broker should start.</p>
                <div className="flex flex-wrap gap-3">
                  {Object.keys(SEV).map((k) => <span key={k} className="inline-flex items-center gap-2 label-mono text-[#595959]"><span className="w-3 h-3 rounded-full" style={{ background: SEV[k].color }} />{SEV[k].label}</span>)}
                </div>
              </div>
              <Matrix />
            </div>
          </div>
        </section>

        {/* 6 — Reflection */}
        <section className="border-b border-[#0A0A0A] bg-[#4C1D95] text-white">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28">
            <p className="label-mono mb-6 text-[#DDD6FE]">/ 06 — Reflection</p>
            <p className="font-display text-3xl md:text-5xl tracking-tighter max-w-4xl leading-[1.05]">Benchmarking isn&apos;t about copying competitors — it&apos;s about seeing, funnel by funnel, exactly where a product loses the users its rivals keep. IDX Broker&apos;s gaps were clear and mostly fixable; the value was ranking them so the team knows what to fix first.</p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-4 label-mono hover:bg-white hover:text-[#0A0A0A] transition-colors" data-testid="idx-cta-book">Book your audit <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
              <Link to="/#work" className="inline-flex items-center gap-2 border border-white px-6 py-4 label-mono hover:bg-white hover:text-[#4C1D95] transition-colors" data-testid="idx-cta-back"><ArrowLeft size={16} strokeWidth={1.5} /> See more work</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
